/* eslint-disable jsx-a11y/alt-text */
import {
  AnchorHTMLAttributes,
  DetailedHTMLProps,
  FC,
  ReactNode,
  useCallback,
} from 'react'

import { Carousel, CarouselProps } from '@mantine/carousel'
import { clsx } from '@mantine/core'

import ChevronLeftSvg from 'components/icons/ChevronLeftSvg'
import ChevronRightSvg from 'components/icons/ChevronRightSvg'
import { DataAttributeKey } from 'interfaces/types'

import {
  StyledCarouselControlInner,
  StyledCarouselTag,
} from './CarouselTag.styled'

export type CarouselTagItem = {
  icon?: string | JSX.Element
  title?: string
  href: string
  itemProps?: Omit<
    DetailedHTMLProps<
      AnchorHTMLAttributes<HTMLAnchorElement>,
      HTMLAnchorElement
    >,
    'href'
  >
}

export type CarouselVerticalProps = CarouselProps & {
  items: CarouselTagItem[]
  itemTracking?: Partial<
    Record<'next' | 'prev', Record<DataAttributeKey, string | undefined>>
  >
  renderItem?: (item: CarouselTagItem, index: number) => ReactNode
}

export const CarouselTag: FC<CarouselVerticalProps> = (props) => {
  const {
    items,
    itemTracking,
    renderItem: renderItemProps,
    containScroll = 'trimSnaps',
    slideSize = 'auto',
    slideGap = 8,
    previousControlIcon = (
      <StyledCarouselControlInner {...itemTracking?.prev}>
        <ChevronLeftSvg className="carouselTagControlIcon" />
      </StyledCarouselControlInner>
    ),
    nextControlIcon = (
      <StyledCarouselControlInner {...itemTracking?.next}>
        <ChevronRightSvg className="carouselTagControlIcon" />
      </StyledCarouselControlInner>
    ),
    ...restProps
  } = props

  const renderItem = useCallback(
    (item: CarouselTagItem, index: number) => {
      return (
        <Carousel.Slide key={`${index}`}>
          {!!renderItemProps ? (
            renderItemProps(item, index)
          ) : (
            <a
              {...item.itemProps}
              href={item.href}
              className={clsx('carouselTagItem', item.itemProps?.className)}
            >
              {item?.icon && (
                <span className="carouselTagIcon">
                  {typeof item.icon === 'string' ? (
                    <img loading="lazy" src={item.icon} alt="" />
                  ) : (
                    item.icon
                  )}
                </span>
              )}
              <span className="carouselTagLabel">{item?.title}</span>
            </a>
          )}
        </Carousel.Slide>
      )
    },
    [renderItemProps],
  )

  if (!items?.length) {
    return null
  }

  return (
    <StyledCarouselTag
      {...restProps}
      containScroll={containScroll}
      slideSize={slideSize}
      slideGap={slideGap}
      previousControlIcon={previousControlIcon}
      nextControlIcon={nextControlIcon}
      className={clsx('carouselTag', restProps?.className)}
    >
      {items.map(renderItem)}
    </StyledCarouselTag>
  )
}
